import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'study-smarter-finals-week',
  title: 'Finals Week Study Plan: 7 Days, One Focus Per Day',
  description:
    'Follow this finals week study plan: one clear job per day for seven days, 45-minute study blocks, a panic reset and a plan for bad days.',
  category: 'Learning',
  icon: 'calendar',
  publishedAt: '2026-10-19',
  author: BLOG_AUTHOR,
  product: 'chot-study',
  keywords: ['finals week study plan', 'how to study for finals', 'exam study schedule', 'active recall'],
  body: `You have seven days, four courses, one highlighter that is nearly dry, and a growing suspicion that you cannot remember anything from week three. The temptation is to open the textbook at page one and start reading. Please don't.

This finals week study plan gives every day one job, so you always know what to do next. It will not make the exam easy. It will make the week manageable, which is most of the battle.

## Ground rules for your finals week study plan

Three habits carry the whole plan.

- **Test yourself, don't only reread.** Closing the book and trying to recall an answer is the core method here. It is uncomfortable because it works. We explain why in [Stop Rereading Your Notes](/blog/stop-rereading-your-notes/).
- **Work in blocks.** Study for about 45 minutes, then take a real 10-minute break: stand up, drink water, look out a window. Your phone is not a break.
- **Protect the basics.** Sleep, real meals, water and a daily walk are not rewards for finishing. They are part of how you remember things. A tired brain studies slowly.

If your whole week is busy with other deadlines too, [Turn Your To-Do List Into a Plan](/blog/turn-your-to-do-list-into-a-plan/) will help you fit this around everything else.

## Day 1: Map the exam and build your study schedule

Today you do not study the hard stuff. You figure out what the exam covers. Gather the syllabus, lecture titles, past papers, and anything your instructor said about format and weighting.

Then list every topic and score each one twice, from 1 to 5: how much of the exam it is likely to be, and how shaky you feel about it. Multiply the two numbers. The highest scores go to the top of your list, and that list drives the rest of the week.

End the day by writing each day's focus into your calendar. Having it in writing means you stop deciding and start doing.

## Day 2: Hardest topic first

Take the topic at the top of your list and give it your freshest hours, which for most people means the morning. Hard material deserves your best attention, not your last scraps of energy.

Read the material once to get oriented, then switch to recall. Close everything and write down what you remember, as a list or a quick sketch. Check against your notes, and circle the gaps. Those circles are your real study guide.

## Day 3: Practice questions and past papers

Today you answer questions instead of consuming content. Do a past paper or a set of practice problems, ideally under loose time pressure, without peeking.

Mark it honestly afterward. For every mistake, write one line: was it a knowledge gap, a careless slip, or a time problem? Those need different fixes, and tomorrow is about the first kind.

## Day 4: Fix the weak spots

Go back to the mistakes from yesterday and the circled gaps from Day 2. Re-learn each one in your own words, then test it again an hour later without notes.

Explaining a concept out loud, as if to a friend who knows nothing about it, is a blunt and effective check. If you stumble, you have found the exact spot to work on. Keep the day tight: fewer topics, done properly.

## Day 5: Mixed review

Exams do not announce which chapter a question comes from, so practice shouldn't either. Shuffle your topics. Make a stack of questions from every unit and work through them in random order.

Mixing feels harder and slower than studying one chapter at a time. That is the point. It trains you to pick the right idea out of your memory, not only the one you read ten minutes ago.

Here is a prompt you can paste into any AI chat tool you already use, or hand to a study partner:

> Here are my notes on [topic]. Ask me one question at a time, mixing easy and hard ones. Wait for my answer, tell me what I got right and wrong, and then move on.

## Day 6: Light recall and logistics

Today is lighter on purpose. Do one short recall session on your top topics, then stop. Cramming new material the day before mostly raises your stress and cuts into your sleep.

Spend the rest of the day on logistics:

- Confirm the time, the room and how you are getting there.
- Pack your bag: ID, pens, calculator, water, a snack.
- Lay out what you will wear.
- Eat a proper dinner, put your notes away, and aim for an early night.

## Day 7: Exam day

Keep the morning simple. Eat something you know sits well with you, drink some water, and skim your one-page summary at most. Do not start a new topic.

Get there early. When the paper arrives, read every question first, then start with the ones you are surest of so you build momentum. Keep an eye on the clock, and if you freeze on a question, mark it and move on. You can come back.

When it is over, take the rest of the day off. You earned it.

## What to do when panic hits

Almost everyone gets a wobble somewhere in this week. A quick reset that works for many people:

1. **Stop and put your feet on the floor.** Notice the chair holding you up.
2. **Breathe slowly.** In through your nose for a count of four, out for a count of six, a few times.
3. **Shrink the task.** Ask "what is the next ten minutes?" and do only that.

Panic often says "I'll never get through this." The plan says "today is Day 4, and Day 4 is weak spots." If you want more tools for keeping your head in a stressful week, [Stoic Tools for a Stressful Week](/blog/stoic-tools-for-a-stressful-week/) is a calm read. And if the anxiety feels bigger than ordinary exam nerves, please talk to someone you trust or a professional. That is not a weakness.

## The minimum viable day

Some days fall apart: you oversleep, a family thing comes up, you feel flat. Don't scrap the plan. Run the minimum viable day instead:

- One 45-minute block on the most important topic for that day.
- Ten minutes of recall: write down what you remember from yesterday.
- A meal, a walk, and an actual bedtime.

That is it. A small day that happened beats a perfect day that didn't. Pick the plan back up tomorrow where you left off, and don't try to repay the missed hours at 2am.

## Where Chot Study fits in

All of this works with a pen, paper and your own discipline. The slow part is usually the setup: turning your notes into questions, summaries and quizzes takes time you don't have in finals week.

That is the job [Chot Study](/products/chot-study/) is designed for. It is an AI study app that turns your own documents into questions, summaries and study games, and lets you chat with your material. It is coming soon and is not available yet, so for this week, use the plan above and a friend with a stack of flashcards.

## You only need the next day

You do not need to feel ready on Day 1. You need to know what Day 1 is, do it, and then do the next one. Map it, hit the hard stuff, practice, repair, mix, rest, and show up.

Start tonight: gather your syllabus, list every topic and score each one from 1 to 5, which takes about 30 minutes. Then read [Stop Rereading Your Notes](/blog/stop-rereading-your-notes/) for the recall method behind Days 2 to 5, and keep an eye on Chot Study, which is coming soon.
`,
};

export default post;
